import type * as React from "react";
import { memo, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { TextField } from "~/common/mui";
import { type STCauttoSuggestData } from "~/pages/StcFinderPage/types";
import { SimpleAutocomplete } from "~/common/components/Select/SimpleAutocompleteSelect";
import { trackSelectedCountrySTC } from "~/app/analytics/ga/ga";
import { Card } from "~/features/configurator/Linear/components/Card";
import { useTranslation } from "react-i18next";

export interface Props {
  searchResults: (country?: string, state?: string, zipCode?: string) => void;
  countries: STCauttoSuggestData[];
  states: STCauttoSuggestData[];
  pageCode: string;
}
export interface STCfinderInputs {
  country: string;
  state: string;
  zipCode: string;
}
const STCfinderSearchCard = (props: Props) => {
  const { searchResults, countries, states, pageCode } = props;
  const { t } = useTranslation();
  const { register, watch, setValue, handleSubmit, control } = useForm<STCfinderInputs>({
    defaultValues: {
      country: pageCode,
      state: "",
      zipCode: "",
    },
  });
  const [showZipCodeInput, setShowZipCodeInput] = useState<boolean>(false);
  const [countryArray, setCountryArray] = useState<STCauttoSuggestData[]>([]);

  const onSubmit = (data: STCfinderInputs) => {
    const fullCountryObject = countries.find((country) => country.value === data.country);
    if (fullCountryObject) {
      trackSelectedCountrySTC(fullCountryObject.stringLabel);
    }

    searchResults(data.country, data.state, data.zipCode);
  };

  useEffect(() => {
    setValue("state", "");
    setValue("zipCode", "");
    setShowZipCodeInput(false);
    if (watch("country") === "DE") {
      setShowZipCodeInput(true);
    }

    handleSubmit(onSubmit)();
  }, [watch("country")]);

  const onKeyDownHandler = (e: React.KeyboardEvent<HTMLFormElement>) => {
    //enter on keybord
    if (e.keyCode === 13) {
      handleSubmit(onSubmit)();
    }
  };

  useEffect(() => {
    handleSubmit(onSubmit)();
  }, [watch("state")]);

  useEffect(() => {
    const remodeledCountries: STCauttoSuggestData[] = [];

    countries.forEach((country: any) => {
      const labelTranslated = t(country.stringLabel);
      remodeledCountries.push({ value: country.value, label: labelTranslated, stringLabel: labelTranslated });
    });

    setCountryArray(remodeledCountries);
  }, [countries]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} onKeyDown={onKeyDownHandler}>
      <Card disableHover={true} className={"scha__stc-finder__search-card"}>
        <div className="stc-finder__search-card__form__input">
          <Controller
            render={({ field: { value, onChange, onBlur } }) => (
              <SimpleAutocomplete
                values={countryArray}
                label={t("STCfinder.table.header.country")}
                fullWidth={true}
                onBlur={onBlur}
                value={value}
                onChange={(value) => onChange(value)}
              />
            )}
            name={"country"}
            control={control}
          />
        </div>
        {states.length > 0 && (
          <div className="stc-finder__search-card__form__input">
            <Controller
              render={({ field: { value, onChange, onBlur } }) => (
                <SimpleAutocomplete
                  values={states}
                  label={t("STCfinder.table.header.state")}
                  fullWidth={true}
                  onBlur={onBlur}
                  value={value}
                  onChange={(value) => onChange(value)}
                />
              )}
              name="state"
              control={control}
              defaultValue={""}
            />
          </div>
        )}
        {showZipCodeInput && (
          <div className="stc-finder__search-card__form__input">
            <Controller
              render={({ field: { ref, ...formProps } }) => (
                <TextField
                  fullWidth
                  label={t("STCfinder.table.header.zipCode")}
                  inputRef={ref}
                  className="stc-finder__search-card__form__input"
                  {...formProps}
                  onBlur={() => {
                    formProps.onBlur();
                    handleSubmit(onSubmit)();
                  }}
                />
              )}
              name="zipCode"
              control={control}
            />
          </div>
        )}
      </Card>
    </form>
  );
};
export default memo(STCfinderSearchCard);
